import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { callFn, consumeOAuthFragment, isAuthed, logout as apiLogout } from '../lib/api';
import type { Bootstrap, Category, Location, Me, ReservationTag, Settings } from '../lib/types';

type ToastKind = 'ok' | 'err' | 'info';
interface Toast { id: number; kind: ToastKind; text: string; }

interface AppState {
  ready: boolean;
  me: Me | null;
  settings: Settings | null;
  locations: Location[];
  categories: Category[];
  reservationTags: ReservationTag[];
  signedIn: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /** Count of the current user's reservations in the 'ready' state — drives the
   *  green badge on the My Items nav link (visible from any page). */
  readyCount: number;
  refreshReadyCount: () => Promise<void>;
  /** Re-fetch the account. Resolves to the sign-in error message when the platform
   *  session is valid but this app rejected the account (not invited / disabled /
   *  wrong domain) — the caller is then signed out and should show that message. */
  reload: () => Promise<string | null>;
  signOut: () => void;
  toast: (text: string, kind?: ToastKind) => void;
  toasts: Toast[];
}

const Ctx = createContext<AppState | null>(null);
export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp must be used within AppProvider');
  return v;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [reservationTags, setReservationTags] = useState<ReservationTag[]>([]);
  const [readyCount, setReadyCount] = useState(0);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(1);

  const toast = useCallback((text: string, kind: ToastKind = 'info') => {
    const id = toastId.current++;
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800);
  }, []);

  // How many of the current user's reservations are 'ready' to pick up.
  // Cheap enough (their own reservations only) to fetch on load + after actions.
  const refreshReadyCount = useCallback(async () => {
    if (!isAuthed()) { setReadyCount(0); return; }
    try {
      const rows = await callFn<{ status: string }[]>('list_my_reservations');
      setReadyCount(rows.filter((r) => r.status === 'ready').length);
    } catch { /* non-fatal — leave the last known count */ }
  }, []);

  const reload = useCallback(async (): Promise<string | null> => {
    if (!isAuthed()) { setMe(null); setReady(true); return null; }
    try {
      const b = await callFn<Bootstrap>('bootstrap');
      setMe(b.me); setSettings(b.settings); setLocations(b.locations); setCategories(b.categories);
      setReservationTags(b.reservation_tags || []);
      void refreshReadyCount();
      return null;
    } catch (e) {
      setMe(null);
      // A valid platform session whose bootstrap fails means the account got
      // authenticated but isn't authorized for THIS app (wrong domain, not
      // invited, disabled). Log out and surface the server's message on login.
      // The sessionStorage slot covers the OAuth return (fresh page load, Login
      // reads it at mount); the return value covers the password form, which is
      // already mounted and would otherwise just "flash" with no message.
      const status = (e as { status?: number } | undefined)?.status;
      const msg = e instanceof Error ? e.message : 'Could not load your account.';
      if (status === 401 || status === 403 || /authorized|forbidden|limited to|invited|disabled/i.test(msg)) {
        apiLogout();
        sessionStorage.setItem('scb.loginError', msg);
        return msg;
      }
      return null;
    } finally { setReady(true); }
  }, [refreshReadyCount]);

  useEffect(() => {
    const oauth = consumeOAuthFragment();
    if (oauth && !oauth.ok && oauth.error) sessionStorage.setItem('scb.loginError', oauth.error);
    void reload();
  }, [reload]);

  const signOut = useCallback(() => { apiLogout(); setMe(null); window.location.assign('/'); }, []);

  const value: AppState = {
    ready, me, settings, locations, categories, reservationTags,
    signedIn: !!me,
    isAdmin: me?.role === 'admin' || me?.role === 'super_admin',
    isSuperAdmin: me?.role === 'super_admin',
    readyCount, refreshReadyCount,
    reload, signOut, toast, toasts,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
